import React, { useEffect, useRef } from 'react';
import { Sparkles, Send, Loader2, MessageSquare } from 'lucide-react';
import { QuickActionPills, QuickAction, ChatOverflowMenu, MenuActions } from '../shared';
import { useClickOutside } from '../../../hooks/chat';
import { CHAT_TEXT } from '../../../constants/chat';

interface ChatBottomBarProps {
  inputMessage: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
  quickActions: QuickAction[];
  messageCount: number;
  onOpenPanel: () => void;
  menuActions: MenuActions;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  adjustTextareaHeight: (textarea: HTMLTextAreaElement | null) => void;
}

/**
 * Horizontal bottom bar shown when the chat panel is closed
 * Contains branding, input, quick actions, and overflow menu
 */
export const ChatBottomBar: React.FC<ChatBottomBarProps> = ({
  inputMessage,
  onInputChange,
  onSend,
  onKeyPress,
  isLoading,
  quickActions,
  messageCount,
  onOpenPanel,
  menuActions,
  inputRef,
  adjustTextareaHeight,
}) => {
  const overflowMenuRef = useRef<HTMLDivElement>(null);
  const [showOverflowMenu, setShowOverflowMenu] = React.useState(false);

  useClickOutside(overflowMenuRef, () => setShowOverflowMenu(false), showOverflowMenu);

  // Auto-resize textarea
  useEffect(() => {
    adjustTextareaHeight(inputRef.current);
  }, [inputMessage, adjustTextareaHeight, inputRef]);

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-20">
      <div className="px-4 py-3">
        {/* Single Row - Branding, Input, Actions */}
        <div className="flex gap-3 items-center">
          {/* Skylarq Branding */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
          </div>

          {/* Input Field */}
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={onKeyPress}
              placeholder={CHAT_TEXT.PLACEHOLDER_FULL}
              className="w-full px-4 py-2.5 text-[14px] bg-gray-50 border border-gray-200 rounded-xl outline-none resize-none placeholder:text-gray-400 focus:border-gray-300 focus:bg-white transition-all"
              style={{ minHeight: '42px', maxHeight: '100px', lineHeight: '1.5' }}
              disabled={isLoading}
              rows={1}
            />
          </div>

          {/* Quick Actions - hidden on mobile to save space */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <QuickActionPills actions={quickActions} variant="compact" />

            {/* Chat History Button */}
            {messageCount > 0 && (
              <button
                onClick={onOpenPanel}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                title="Open chat history"
              >
                <MessageSquare className="w-3 h-3" />
                <span>{messageCount}</span>
              </button>
            )}

            {/* Overflow Menu */}
            <ChatOverflowMenu
              ref={overflowMenuRef}
              isOpen={showOverflowMenu}
              onToggle={() => setShowOverflowMenu(!showOverflowMenu)}
              onClose={() => setShowOverflowMenu(false)}
              actions={menuActions}
              position="above"
              buttonSize="md"
            />

            {/* Send Button */}
            <button
              onClick={onSend}
              disabled={!inputMessage.trim() || isLoading}
              className="w-9 h-9 bg-gray-900 rounded-xl flex items-center justify-center transition-all hover:bg-gray-800 disabled:bg-gray-200 disabled:cursor-not-allowed flex-shrink-0"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              ) : (
                <Send className="w-4 h-4 text-white" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
